import * as React from "react";
import { LinearProgress } from "@mui/material";
import { PageContainer } from "#pages/PageContainer";
import { Json } from "#common/Json";
import { useQueryParams } from "#components/App/routing/useQueryParams";
import { useAppDispatch, useAppSelector } from "#store";
import { generatePath, Navigate } from "react-router";
import { Routes } from "#/config";
import { stringToEnumValue } from "#/util/enums";
import { DatasourceEnum } from "#/services/pilotService/types";
import { pilotService } from "#services";
import { useQuery } from "react-query";
import { H1, P } from "#common/headings";
import {
    selectEnvServiceUrl,
    selectUiDatasource,
    selectUiUserId,
} from "#selectors";
import { setQuery } from "#store/ui";
import { ActionType } from "#services/pilotService/types";
import { FilterOperatorType } from "#/store/actions/actionTypes";

type Params = {
    query: string;
    datasource: DatasourceEnum;
    op: ActionType;
    column: string;
    value: string;
    operator: FilterOperatorType;
};

function getParams(params: URLSearchParams, defaults: Partial<Params>): Params {
    const q = String(params.get("q") ?? defaults.query).trim();
    const ds = stringToEnumValue<typeof DatasourceEnum, DatasourceEnum>(
        DatasourceEnum,
        String(params.get("db")).toUpperCase()
    );
    const at = stringToEnumValue<typeof ActionType, ActionType>(
        ActionType,
        String(params.get("op")).toUpperCase()
    );
    const column = String(params.get("column") ?? defaults.column).trim();
    const operator = stringToEnumValue<
        typeof FilterOperatorType,
        FilterOperatorType
    >(FilterOperatorType, String(params.get("operator")).toUpperCase());
    const value = String(params.get("value") ?? defaults.value).trim();

    return {
        query: q ?? "",
        datasource: ds ?? defaults.datasource ?? DatasourceEnum.cordis,
        op: at ?? defaults.op ?? ActionType.NL2SQL,
        column: column ?? defaults.column ?? "",
        operator: operator ?? defaults.operator ?? FilterOperatorType.EQUAL,
        value: value ?? defaults.value ?? "",
    };
}

export const TriggerActionPage = () => {
    const q: URLSearchParams = useQueryParams();
    const userId = useAppSelector(selectUiUserId);
    const url = useAppSelector(selectEnvServiceUrl);
    const uiDatasource = useAppSelector(selectUiDatasource);
    const dispatch = useAppDispatch();

    const { query, datasource, op, column, value, operator } = React.useMemo(
        () =>
            getParams(q, {
                query: "",
                datasource: uiDatasource.name,
                op: ActionType.NL2SQL,
            }),
        [q, uiDatasource.name]
    );

    const sMaxIn = useAppSelector(
        state => state.uiState.maxInterpretationsLimit
    );

    const actionTrigger = useQuery(
        [datasource, query, sMaxIn, op, column, value],
        () => {
            const service = pilotService.actionService(url, userId);
            switch (op) {
                default:
                case ActionType.NL2SQL:
                case ActionType.NL2SPARQL:
                    return service.nl2ql(query, datasource, sMaxIn);
                case ActionType.QL2DATA:
                case ActionType.SQL2DATA:
                    return service.sql2data(query, datasource);
                case ActionType.BY_RECOMMENDATION:
                    return service.recommendations(query, datasource, sMaxIn);
                case ActionType.QL_FILTER:
                    return service.exploreByFilter({
                        column,
                        value,
                        query: query,
                        operator: operator ?? "=",
                        datasource: datasource,
                        maxInterpretations: sMaxIn,
                    });
                case ActionType.QL_FACET:
                    return service.exploreByFacet({
                        column,
                        query: query,
                        datasource: datasource,
                        maxInterpretations: sMaxIn,
                    });
                case ActionType.QL_SUPERSET:
                    return service.exploreBySuperset({
                        query: query,
                        datasource: datasource,
                        maxInterpretations: sMaxIn,
                    });
            }
        }
    );
    React.useEffect(() => {
        dispatch(setQuery(query));
    }, [dispatch, query]);

    return (
        <PageContainer>
            <H1>Search</H1>
            {actionTrigger.isLoading ? <LinearProgress /> : null}
            {actionTrigger.isError ? (
                <>
                    <P>Error submitting request</P>
                    <Json src={actionTrigger.error} />
                </>
            ) : null}
            {actionTrigger.isSuccess ? (
                <Navigate
                    to={generatePath(Routes.action.path, {
                        id: String(actionTrigger.data.id),
                    })}
                />
            ) : null}
        </PageContainer>
    );
};
